import { useMutation, useQuery } from "@tanstack/react-query";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { CreateWeekPayload, StaticLoadResponse, WeekInfo } from "app/data/static-load/model";
import { toast } from "react-toastify";

export const useWeeks = () => {
  const { data, isLoading, refetch } =
    useQuery({
      queryKey: [QUERY_KEYS.GET_WEEKS],
      queryFn: async () => {

        const response = await apiQueryAdapter.query<WeekInfo[]>(`/manifest-management/weeks?division=1`, // Floor covering only
          "GET",
        );

        return response.data;

      },
      refetchOnWindowFocus: false,
      retry: false,
    })

  return { data, isLoading, refetch };
}

export const useCreateWeek = (onSuccess?: () => void) => {
  return useMutation<
    StaticLoadResponse, // response type
    Error, // error type
    CreateWeekPayload  // variables type
  >({
    mutationFn: async (payload) => {
      const response = await apiQueryAdapter.query<StaticLoadResponse>(
        `/manifest-management/weeks`,
        "POST",
        { body: payload }
      );

      return response.data;
    },
    onSuccess: () => {
      onSuccess?.();
      toast.info("Weeks created successfully");
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });
};

export const useUpdateWeek = (onSuccess?: () => void) => {
  return useMutation<
    StaticLoadResponse, // response type
    Error, // error type
    CreateWeekPayload  // variables type
  >({
    mutationFn: async (payload) => {
      const response = await apiQueryAdapter.query<StaticLoadResponse>(
        `/manifest-management/weeks`,
        "PUT",
        { body: payload }
      );

      return response.data;
    },
    onSuccess: () => {
      onSuccess?.();
      toast.info("Weeks updated successfully");
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });
};

export const useDeleteWeek = (onSuccess?: () => void) => {
  return useMutation(
    async (payload: { division: string; weekStartDate: string }) => {
      await apiQueryAdapter.query(
        `/manifest-management/weeks/${payload.division}/${payload.weekStartDate}`,
        "DELETE"
      );
    },
    {
      onSuccess: () => {
        onSuccess?.();
        toast.info("Week deleted successfully");
      },
      onError: (e: any) => {
        if (e instanceof Error) {
          toast.error(e.message);
        }
        else if (e?.response?.data?.message) {
          toast.error(e.response.data.message);
        }
        else {
          toast.error(ERROR_MESSAGES.COMMON);
        }
      },
    }
  );
};
